import { useMemo, useState, useEffect } from "react";
import { useApp } from "../context/AppContext";

const API_BASE = import.meta.env.VITE_API_URL || "";

export default function AddServerMentionMappingModal({
  onClose,
  guilds,
  assignments,
  setAssignments,
  refreshGuilds
}) {
  const { models, showToast } = useApp();
  const [guildId, setGuildId] = useState("");
  const [modelId, setModelId] = useState("");
  const [saving, setSaving] = useState(false);

  const guildList = Array.isArray(guilds) ? guilds : [];


  const guildOptions = useMemo(() => {
    const list = Array.isArray(guilds) ? guilds : [];
    // Not set first, then already-set (so replacement is less "accidental")
    return list.slice().sort((a, b) => {
      const aSet = !!(assignments && assignments[a.id]);
      const bSet = !!(assignments && assignments[b.id]);
      if (aSet !== bSet) return aSet ? 1 : -1;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [guilds, assignments]);

  useEffect(() => {
    if (!guildId && guildOptions.length > 0) setGuildId(guildOptions[0].id);
  }, [guildId, guildOptions]);

  const flatModels = useMemo(() => {
    const groups = Array.isArray(models) ? models : [];
    return groups.flatMap(pg =>
      (pg.models || []).map(m => ({ ...m, provider: pg.provider }))
    );
  }, [models]);

  const enabledModels = useMemo(() => {
    const list = flatModels.filter(m => !!m.enabled);
    // Custom models first, then alphabetical
    return list.slice().sort((a, b) => {
      const ac = !!a.isCustom;
      const bc = !!b.isCustom;
      if (ac !== bc) return ac ? -1 : 1;
      return String(a.displayName || "").localeCompare(String(b.displayName || ""));
    });
  }, [flatModels]);

  const selectedGuild = useMemo(
    () => guildOptions.find(g => String(g.id) === String(guildId)),
    [guildOptions, guildId]
  );

  const selectedModel = useMemo(
    () => enabledModels.find(m => String(m.id) === String(modelId)),
    [enabledModels, modelId]
  );

  const currentName =
    guildId && assignments ? (assignments[guildId]?.display_name || "") : "";
  const isReplace = !!currentName;

  async function handleSave() {
    if (!selectedGuild) return showToast("Select a server", "error");
    if (!selectedModel) return showToast("Select a model", "error");

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/servers/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          guild_id: selectedGuild.id,
          guild_name: selectedGuild.name,
          guild_icon: selectedGuild.icon,
          user_model_id: selectedModel.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) throw new Error(data?.error || "Failed to save");

      setAssignments((prev) => ({
        ...(prev || {}),
        [selectedGuild.id]: {
          user_model_id: selectedModel.id,
          display_name: selectedModel.displayName
        },
      }));
      showToast(isReplace ? "Mapping updated" : "Mapping added", "success");
      if (refreshGuilds) refreshGuilds();
      onClose();
    } catch (e) {
      showToast(e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "560px" }}>
        <div className="modal-header">
          <h2>{isReplace ? "Update server @mentions" : "Add server @mentions"}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div style={{ fontSize: "0.9rem", color: "#a1a1aa", lineHeight: "1.45", marginBottom: "0.75rem" }}>
            Select a server and a model. When members @mention the bot in that server, the selected model (and its prompt) will reply.
          </div>

          <div
            style={{
              marginBottom: "1rem",
              padding: "0.9rem 1rem",
              borderRadius: "10px",
              background: "rgba(245, 158, 11, 0.08)",
              border: "1px solid rgba(245, 158, 11, 0.28)",
              color: "#d4d4d8",
              fontSize: "0.875rem",
              lineHeight: "1.45"
            }}
          >
            <div style={{ fontWeight: 800, color: "#fbbf24", marginBottom: "0.35rem" }}>
              Attention!
            </div>
            <div style={{ marginTop: "0.25rem", color: "#a1a1aa" }}>
              Anyone in this server can @mention the bot, and replies use the server owner’s plan limits (Free: 20/day) and balance/costs.
            </div>
            <div style={{ marginTop: "0.4rem", color: "#a1a1aa" }}>
              This only affects @mentions. /chat still uses each person’s own account.
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: "0.75rem" }}>
            <label className="form-label">Mapping</label>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
              <div style={{ position: "relative", flex: "1 1 220px", maxWidth: "360px" }}>
                <select
                  className="select"
                  value={guildId}
                  onChange={(e) => setGuildId(e.target.value)}
                  disabled={saving}
                  style={{
                    paddingRight: "2.2rem",
                    fontSize: "0.9375rem",
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none"
                  }}
                >
                  {guildOptions.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <div
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    color: "var(--text-muted)",
                    fontSize: "0.85rem"
                  }}
                >
                  ▼
                </div>
              </div>

              <div style={{ color: "rgba(255,255,255,0.22)", fontWeight: 900, padding: "0 0.15rem" }}>
                →
              </div>

              <div style={{ position: "relative", flex: "1 1 220px", maxWidth: "360px" }}>
                <select
                  className="select"
                  value={modelId}
                  onChange={(e) => setModelId(e.target.value)}
                  disabled={saving}
                  style={{
                    paddingRight: "2.2rem",
                    fontSize: "0.9375rem",
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none"
                  }}
                >
                  <option value="">Select a model...</option>
                  {enabledModels.map((m) => (
                    <option
                      key={m.id}
                      value={m.id}
                      disabled={String(m.id) === String(modelId)}
                    >
                      {String(m.displayName || "").length > 48
                        ? String(m.displayName).slice(0, 48) + "…"
                        : m.displayName}
                    </option>
                  ))}
                </select>
                <div
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    color: "var(--text-muted)",
                    fontSize: "0.85rem"
                  }}
                >
                  ▼
                </div>
              </div>
            </div>

            <div style={{ fontSize: "0.8125rem", color: "#9ca3af", marginTop: "0.5rem" }}>
              Current: {currentName ? currentName : "Not set"}
            </div>
            {isReplace && (
              <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                This will replace the current model for this server.
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !guildId || !modelId}>
            {saving ? "Saving..." : (isReplace ? "Update" : "Add")}
          </button>
        </div>
      </div>
    </div>
  );
}

